import {
  useGetReceiptBatchPhoenix,
  useGetResidentListPhoenix,
  useServiceProviderResidentListPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import type {
  GetReceiptBatchResultDto,
  ReceiptBatchControllerResultTypes,
  Gender,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { useAuthenticationInternal } from '@pflegenavi/frontend/authentication';
import { getGlobalServiceProviderIdFromToken } from '../../../utils/getGlobalServiceProviderIdFromToken';
import { ResidentState } from '@pflegenavi/shared/api';
import type { BatchReceiptDetails } from '../form/ServiceProviderReceiptBatchForm';
import { useThrottledReceiptBatch } from '../form/utils/useThrottledReceipts';
import { v4 as uuid } from 'uuid';

interface UseAugmentedThrottledReceiptsProps {
  batchId: string | undefined;
}

interface UseAugmentedThrottledReceiptsResult {
  throttledReceipts: BatchReceiptDetails[];
  isThrottling: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  errorUpdateCount: number;
  isRefetching: boolean;
  batch?: GetReceiptBatchResultDto & { receiptImagesRequired: boolean };
  totalNumberOfReceipts: number;
  totalAmount: number;
  refetchBatch: () => void;
  minReceiptDate?: Date;
  maxReceiptDate?: Date;
}

export const useAugmentedThrottledReceipts = ({
  batchId,
}: UseAugmentedThrottledReceiptsProps): UseAugmentedThrottledReceiptsResult => {
  const {
    throttledReceipts,
    isThrottling,
    batch,
    isLoading,
    isError,
    error,
    errorUpdateCount,
    isRefetching,
    totalNumberOfReceipts,
    totalAmount,
    refetchBatch,
    minReceiptDate,
    maxReceiptDate,
  } = useThrottledReceiptBatch<
    ReceiptBatchControllerResultTypes['getReceiptBatch'] & {
      receiptImagesRequired: boolean;
    }
  >({
    batchId,
    useGetReceiptBatch: useGetReceiptBatchPhoenix,
  });

  const { getTokenImmediate } = useAuthenticationInternal();

  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );

  const { data: residentsWithReceiptsEnabled } = useGetResidentListPhoenix(
    {
      nursingHomeId: batch?.nursingHomeId ?? '',
      globalServiceProviderId: globalServiceProviderId!,
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: 0,
      refetchIntervalInBackground: false,
      enabled: !!globalServiceProviderId && !!batch?.nursingHomeId,
    }
  );

  const residentsWithReceiptsEnabledById = useMemo(() => {
    return keyBy(residentsWithReceiptsEnabled ?? [], 'id');
  }, [residentsWithReceiptsEnabled]);

  const idsOfMissingResidents = useMemo(() => {
    return throttledReceipts
      .filter((receipt) => receipt.residentId)
      .map((receipt) => receipt.residentId!)
      .filter((residentId) => !residentsWithReceiptsEnabledById[residentId]);
  }, [throttledReceipts, residentsWithReceiptsEnabledById]);

  const { data: residentsWithReceiptsDisabled } =
    useServiceProviderResidentListPhoenix(
      {
        tenant: batch?.tenant ?? '',
        nursingHomeId: batch?.nursingHomeId ?? '',
        residentStates: [
          ResidentState.awaitingSettlement,
          ResidentState.archived,
        ],
        residentIds: idsOfMissingResidents,
      },
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: 0,
        refetchIntervalInBackground: false,
        enabled:
          !!batch?.tenant &&
          !!batch?.nursingHomeId &&
          idsOfMissingResidents.length > 0,
      }
    );

  const residentsWithReceiptsDisabledById = useMemo(() => {
    return keyBy(residentsWithReceiptsDisabled ?? [], 'id');
  }, [residentsWithReceiptsDisabled]);

  const mappedReceipts = useMemo(() => {
    return throttledReceipts.map((receipt) => {
      if (!receipt.residentId) {
        return receipt;
      }

      let residentActive: boolean | undefined = true;
      let resident = residentsWithReceiptsEnabledById[receipt.residentId];

      if (!resident) {
        const phoenixResident =
          residentsWithReceiptsDisabledById[receipt.residentId];
        if (phoenixResident) {
          // Cast PhoenixResident to match GetResidentResultEntryDto type
          resident = {
            ...phoenixResident,
            gender: phoenixResident.gender as Gender,
          };
        }
        residentActive = !resident;
      }

      return {
        ...receipt,
        residentName: receipt.residentName,
        residentActive: residentActive,
        residentGender: receipt.residentGender,
        individualDate: receipt.individualDate,
        lineItems:
          receipt.lineItems.length === 0
            ? [
                {
                  id: undefined,
                  _internalId: `new-${uuid()}`,
                  amount: undefined,
                  count: 1,
                  name: undefined,
                  taxes: undefined,
                },
              ]
            : receipt.lineItems,
      };
    });
  }, [
    throttledReceipts,
    residentsWithReceiptsEnabledById,
    residentsWithReceiptsDisabledById,
  ]);

  return {
    batch,
    totalNumberOfReceipts,
    totalAmount,
    isLoading,
    isError,
    error,
    errorUpdateCount,
    isRefetching,
    isThrottling,
    throttledReceipts: mappedReceipts,
    refetchBatch,
    minReceiptDate,
    maxReceiptDate,
  };
};
